"use client";

/* eslint-disable @next/next/no-img-element */
import { Ruler, X } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * Tombol "Panduan Ukuran" + popup fotonya (mig 5890).
 *
 * Fotonya BUKAN aset terpisah: admin menunjuk salah satu FOTO PRODUK di tab
 * Foto (`/admin/products/new`), dan foto itu tetap ikut tampil di galeri —
 * popup ini jalan pintas ke foto yang sudah tayang, bukan galeri kedua.
 *
 * Dipisah dari `ProductDetail` (1.800+ baris) dengan sengaja: yang dibutuhkan
 * di sana cuma satu baris JSX, sedangkan state buka/tutup + kunci scroll +
 * Escape tak ada sangkut pautnya dengan varian/harga/keranjang.
 *
 * Tanpa `imageUrl` komponen memulangkan `null` — halaman produk yang adminnya
 * belum menunjuk foto TIDAK menumbuhkan tombol kosong.
 */
export function SizeGuideButton({
  imageUrl,
  productName,
}: {
  imageUrl?: string | null;
  productName: string;
}) {
  const [open, setOpen] = useState(false);
  const src = typeof imageUrl === "string" ? imageUrl.trim() : "";

  // Escape menutup popup. Efek hanya terpasang selagi terbuka supaya halaman
  // produk (yang juga punya dialog varian & keranjang) tak menumpuk listener
  // global untuk popup yang tak kelihatan.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!src) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // `shrink-0` + `whitespace-nowrap`: tetangganya di baris ini adalah
        // QuantityStepper selebar 9rem yang TIDAK boleh terjepit di layar
        // sempit — yang mengalah judul "Variasi"/"Jumlah" di sebelah kiri.
        className="flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-shopee/40 bg-shopeeSoft/60 px-2.5 text-xs font-extrabold text-shopee"
        aria-haspopup="dialog"
      >
        <Ruler size={14} />
        Panduan Ukuran
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Panduan ukuran"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-soft"
            // Mengetuk latar menutup popup; ketukan DI DALAM panel tidak —
            // tanpa ini menggeser/zoom foto ikut menutupnya.
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
              <h2 className="text-sm font-extrabold text-ink">Panduan Ukuran</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid h-8 w-8 place-items-center rounded-full text-muted"
                aria-label="Tutup panduan ukuran"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabel ukuran bisa BERAPA PUN rasionya (umumnya jangkung), jadi
                fotonya dirender pada rasio aslinya di dalam wadah yang bisa
                digulir — bukan `object-contain` dalam kotak tetap yang
                mengecilkan angka sampai tak terbaca. `<img>` polos dipakai
                dengan sadar: `next/image` menuntut width+height yang di sini
                tak diketahui, sementara fotonya sudah dikompres saat unggah
                (`compressImageFile`) dan popup ini bukan kandidat LCP. */}
            <div className="overflow-y-auto overscroll-contain">
              <img
                src={src}
                alt={`Panduan ukuran ${productName}`}
                className="h-auto w-full"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
